/**
 * Puce — une pilule : un CHOIX qu'on allume (un filtre, une fenêtre « 30 j »),
 * ou une ÉTIQUETTE qu'on retire (un filtre actif, un tag posé).
 *
 *   <Puce actif={jours === 30} onClick={() => setJours(30)}>30 jours</Puce>
 *   <Puce onRetirer={() => retirer("payeurs")} retirerLabel="Retirer « Payeurs »">Payeurs seulement</Puce>
 *   <Puce couleur="#ec4899" onRetirer={…}>VIP</Puce>            ← la vraie couleur du tag, en point
 *
 *   <ChoixDePuces options={MEDIAS} valeur={media} onChange={setMedia} nom="Média" />
 *   <ChoixDePuces multiple options={LANGUES} valeurs={langues} onChange={setLangues} nom="Langues" />
 *   <FiltresActifs filtres={[{ cle, etiquette, onRetirer }]} onToutRetirer={…} toutRetirerLabel="Tout effacer" />
 *
 * Elle ne se dessine pas elle-même (Kayla, 02-11 : le style des boutons et des
 * pastilles va changer, il doit passer partout sans retouche). Un choix est un
 * `Button` — `accent` quand il est choisi (la marque : ce qui est CHOISI),
 * `secondary` sinon —, une étiquette est un `Badge` gris avec sa croix (un
 * `Button` fantôme). Une étiquette qui a sa propre couleur (un rôle, un tag) la
 * porte en POINT, en aplat plein : c'est sa donnée, pas un style.
 *
 * Successeur des `Chip` / `Pastille` de `CaptionsFilterPopover`, `TeamFiltresPopover`,
 * `LeadsFiltresPopover` et des fenêtres 7/30/90 j de `MonLienTab`.
 */
import { useRef, type KeyboardEvent, type ReactNode } from "react";

import { Badge } from "../Badge";
import { Button, type ButtonSize } from "../Button";
import { X } from "../icons";
import { cn } from "../utils";
import "./saisie.css";

export type TaillePuce = "sm" | "md" | "lg";

const TAILLE_DU_BOUTON: Record<TaillePuce, ButtonSize> = { sm: "sm", md: "md", lg: "lg" };

export interface PuceProps {
  children: ReactNode;
  /** Choisie : le `Button` à la marque (`accent`), `aria-pressed`. Avec `onClick`. */
  actif?: boolean;
  /** La puce devient un bouton qui bascule. */
  onClick?: () => void;
  /** Une croix au bout : la puce est une étiquette qu'on retire. */
  onRetirer?: () => void;
  /** L'étiquette de la croix pour un lecteur d'écran — déjà traduite (défaut « Retirer »). */
  retirerLabel?: string;
  /** Une icône de `@dozka/ui/icons`, à gauche. */
  icone?: ReactNode;
  /** La couleur propre de ce qu'elle désigne (un rôle, un tag) : un point plein à gauche. */
  couleur?: string;
  /** Un nombre en retrait, après le texte : « Français 12 ». */
  compte?: number | string;
  /** sm · md (défaut) · lg — les tailles du `Button`. */
  taille?: TaillePuce;
  disabled?: boolean;
  title?: string;
  className?: string;
}

/** Le point de couleur d'une étiquette qui a la sienne. */
function Point({ couleur }: { couleur: string }) {
  return <span className="si-puce-point si-rond" style={{ background: couleur }} aria-hidden />;
}

/** Le contenu d'une puce : point, icône, texte, compte. */
function Contenu({ icone, couleur, compte, children }: Pick<PuceProps, "icone" | "couleur" | "compte" | "children">) {
  return (
    <>
      {couleur !== undefined && <Point couleur={couleur} />}
      {icone !== undefined && <span className="si-puce-icone flex" aria-hidden>{icone}</span>}
      <span className="si-puce-texte">{children}</span>
      {compte !== undefined && <span className="si-puce-compte">{compte}</span>}
    </>
  );
}

export function Puce({
  children,
  actif,
  onClick,
  onRetirer,
  retirerLabel = "Retirer",
  icone,
  couleur,
  compte,
  taille = "md",
  disabled,
  title,
  className,
}: PuceProps) {
  if (onRetirer) {
    return (
      <Badge
        ton="gris"
        size={taille === "sm" ? "sm" : "md"}
        title={title}
        className={cn("si-puce-etiquette", taille === "lg" && "si-puce-etiquette-lg", disabled && "opacity-50", className)}
        icon={couleur !== undefined ? <Point couleur={couleur} /> : icone}
      >
        <span className="si-puce-texte">{children}</span>
        {compte !== undefined && <span className="si-puce-compte">{compte}</span>}
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={retirerLabel}
          title={retirerLabel}
          disabled={disabled}
          onClick={onRetirer}
          className="si-puce-croix"
        >
          <X weight="bold" aria-hidden />
        </Button>
      </Badge>
    );
  }

  if (onClick) {
    return (
      <Button
        variant={actif ? "accent" : "secondary"}
        size={TAILLE_DU_BOUTON[taille]}
        aria-pressed={!!actif}
        disabled={disabled}
        title={title}
        onClick={onClick}
        className={cn("si-puce", className)}
      >
        <Contenu icone={icone} couleur={couleur} compte={compte}>
          {children}
        </Contenu>
      </Button>
    );
  }

  return (
    <Badge ton={actif ? "marque" : "gris"} size={taille === "sm" ? "sm" : "md"} title={title} className={className} icon={couleur !== undefined ? <Point couleur={couleur} /> : icone}>
      <span className="si-puce-texte">{children}</span>
      {compte !== undefined && <span className="si-puce-compte">{compte}</span>}
    </Badge>
  );
}

/* ── Un choix parmi des puces ─────────────────────────────────────────────── */

export interface OptionPuce {
  valeur: string;
  etiquette: ReactNode;
  icone?: ReactNode;
  couleur?: string;
  compte?: number | string;
  desactive?: boolean;
}

interface ChoixCommun {
  options: readonly OptionPuce[];
  /** Le nom du groupe pour un lecteur d'écran (la section qui le titre le donne aux yeux). */
  nom?: string;
  taille?: TaillePuce;
  disabled?: boolean;
  className?: string;
}

export type ChoixDePucesProps = ChoixCommun &
  (
    | { multiple?: false; valeur: string; onChange: (valeur: string) => void; valeurs?: never }
    | { multiple: true; valeurs: readonly string[]; onChange: (valeurs: string[]) => void; valeur?: never }
  );

/**
 * Des puces qui vont à la ligne. Une seule choisie (`role="radiogroup"`, flèches
 * ← → comme des radios), ou plusieurs avec `multiple` (chacune bascule, `aria-pressed`).
 * Pour un choix unique de deux à quatre mots courts sur une ligne, les `Onglets`
 * segments suffisent ; ChoixDePuces, c'est quand la liste va à la ligne.
 */
export function ChoixDePuces(props: ChoixDePucesProps) {
  const { options, nom, taille = "md", disabled, className } = props;
  const groupe = useRef<HTMLDivElement>(null);

  if (props.multiple) {
    const choisies = new Set(props.valeurs);
    const basculer = (v: string) => {
      const suivant = choisies.has(v) ? props.valeurs.filter((x) => x !== v) : [...props.valeurs, v];
      props.onChange(suivant);
    };
    return (
      <div role="group" aria-label={nom} className={cn("flex flex-wrap gap-1.5", className)}>
        {options.map((o) => (
          <Puce
            key={o.valeur}
            taille={taille}
            icone={o.icone}
            couleur={o.couleur}
            compte={o.compte}
            disabled={disabled || o.desactive}
            actif={choisies.has(o.valeur)}
            onClick={() => basculer(o.valeur)}
          >
            {o.etiquette}
          </Puce>
        ))}
      </div>
    );
  }

  const { valeur, onChange } = props;
  const prenables = options.filter((o) => !o.desactive);
  const indexChoisi = options.findIndex((o) => o.valeur === valeur);

  function auClavier(e: KeyboardEvent<HTMLDivElement>) {
    const sens = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    const bout = e.key === "Home" ? 0 : e.key === "End" ? prenables.length - 1 : -1;
    if (sens === 0 && bout < 0) return;
    e.preventDefault();
    if (prenables.length === 0) return;
    const ici = prenables.findIndex((o) => o.valeur === valeur);
    const i = bout >= 0 ? bout : (ici + sens + prenables.length) % prenables.length;
    const cible = prenables[i]!;
    onChange(cible.valeur);
    groupe.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[options.indexOf(cible)]?.focus();
  }

  return (
    <div ref={groupe} role="radiogroup" aria-label={nom} className={cn("flex flex-wrap gap-1.5", className)} onKeyDown={auClavier}>
      {options.map((o) => {
        const choisie = o.valeur === valeur;
        // Le Tab entre sur la choisie, ou sur la première si aucune ne l'est (le radio natif).
        const tabulable = choisie || (indexChoisi < 0 && o === prenables[0]);
        return (
          <Button
            key={o.valeur}
            role="radio"
            aria-checked={choisie}
            tabIndex={tabulable ? 0 : -1}
            variant={choisie ? "accent" : "secondary"}
            size={TAILLE_DU_BOUTON[taille]}
            disabled={disabled || o.desactive}
            className="si-puce"
            onClick={() => onChange(o.valeur)}
          >
            <Contenu icone={o.icone} couleur={o.couleur} compte={o.compte}>
              {o.etiquette}
            </Contenu>
          </Button>
        );
      })}
    </div>
  );
}

/* ── La rangée des filtres actifs ─────────────────────────────────────────── */

export interface FiltreActif {
  cle: string;
  etiquette: ReactNode;
  onRetirer: () => void;
  couleur?: string;
}

export interface FiltresActifsProps {
  filtres: readonly FiltreActif[];
  /** Un dernier bouton qui lève tout — absent s'il n'y a qu'un filtre. */
  onToutRetirer?: () => void;
  toutRetirerLabel?: string;
  /** L'étiquette de chaque croix, déjà traduite : `(f) => \`Retirer « ${f} »\``. */
  retirerLabel?: (etiquette: ReactNode) => string;
  taille?: TaillePuce;
  className?: string;
}

/**
 * Ce qui filtre la liste, en clair, chacun défaisable seul — la rangée au-dessus
 * d'une liste filtrée, et l'état vide qui doit dire QUEL filtre l'a vidée
 * (Kayla, 17-08 : « IRL » coché, zéro carte, et pour seule sortie « Effacer les
 * filtres »). Rien quand aucun filtre n'est actif.
 */
export function FiltresActifs({
  filtres,
  onToutRetirer,
  toutRetirerLabel = "Tout effacer",
  retirerLabel = (e) => (typeof e === "string" ? `Retirer « ${e} »` : "Retirer"),
  taille = "md",
  className,
}: FiltresActifsProps) {
  if (filtres.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {filtres.map((f) => (
        <Puce key={f.cle} taille={taille} couleur={f.couleur} onRetirer={f.onRetirer} retirerLabel={retirerLabel(f.etiquette)}>
          {f.etiquette}
        </Puce>
      ))}
      {onToutRetirer && filtres.length > 1 && (
        <Button variant="ghost" size={TAILLE_DU_BOUTON[taille]} onClick={onToutRetirer}>
          {toutRetirerLabel}
        </Button>
      )}
    </div>
  );
}
